import type { Graphics } from 'pixi.js';
import type { Island } from './world.js';
import { isInsideIsland } from './world.js';
import { drawPerson } from './person.js';

function makeRng(seed: number): () => number {
  let s = seed;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SHIRT_COLORS = [0xe04040, 0x40b060, 0x2a9d8f, 0xe0d040, 0x40c0c0, 0xe08030, 0x4080e0, 0xd4a017];

export interface Villager {
  x: number;
  y: number;
  angle: number;
  wanderTimer: number;
  walkPhase: number;
  islandIdx: number;
  shirtColor: number;
  hairStyle: number;
  scale: number;
}

/** Purely decorative, ambient NPCs. Every client generates the same set from
 *  the same seed (like the islands themselves), so they're "visible to all
 *  players" without costing a single byte of network traffic. */
export function generateVillagers(islands: Island[]): Villager[] {
  const rng = makeRng(0xf00dba11);
  const villagers: Villager[] = [];

  for (let i = 0; i < islands.length; i++) {
    const isl = islands[i];
    const count = 6 + Math.floor(rng() * 3); // 1-3 base, +5 more per island → 6-8
    for (let j = 0; j < count; j++) {
      for (let attempt = 0; attempt < 30; attempt++) {
        const a = rng() * Math.PI * 2;
        const d = rng() * isl.radius * 0.5;
        const x = isl.cx + Math.cos(a) * d;
        const y = isl.cy + Math.sin(a) * d;
        if (isInsideIsland({ x, y }, isl)) {
          villagers.push({
            x, y,
            angle: rng() * Math.PI * 2,
            wanderTimer: rng() * 120,
            walkPhase: 0,
            islandIdx: i,
            shirtColor: SHIRT_COLORS[Math.floor(rng() * SHIRT_COLORS.length)],
            hairStyle: Math.floor(rng() * 3),
            scale: 0.85 + rng() * 0.25,
          });
          break;
        }
      }
    }
  }
  return villagers;
}

const WALK_SPEED = 0.16;

export function updateVillagers(villagers: Villager[], islands: Island[], dt: number): void {
  for (const v of villagers) {
    v.wanderTimer -= dt;
    if (v.wanderTimer <= 0) {
      v.angle = Math.random() * Math.PI * 2;
      v.wanderTimer = 100 + Math.random() * 240;
    }
    const nx = v.x + Math.cos(v.angle) * WALK_SPEED * dt;
    const ny = v.y + Math.sin(v.angle) * WALK_SPEED * dt;
    const island = islands[v.islandIdx];
    if (island && isInsideIsland({ x: nx, y: ny }, island)) {
      v.x = nx;
      v.y = ny;
      v.walkPhase += 0.18 * dt;
    } else {
      v.angle += Math.PI + (Math.random() - 0.5) * 1.2;
      v.wanderTimer = 40 + Math.random() * 80;
    }
  }
}

export interface ViewBounds { x0: number; y0: number; x1: number; y1: number }

export function drawVillagers(g: Graphics, villagers: Villager[], view?: ViewBounds): void {
  for (const v of villagers) {
    if (view && (v.x < view.x0 || v.x > view.x1 || v.y < view.y0 || v.y > view.y1)) continue;
    drawPerson(g, v.x, v.y, v.shirtColor, v.hairStyle, v.walkPhase, v.scale);
  }
}
